import { EaVerifyLink } from "./analytics/EaDashboardCard";
import React, { useState } from "react";
import { fmtDateTime } from "@/lib/format";
import { AlertTriangle, CheckCircle2, ChevronDown, Clock, Loader2, XCircle } from "lucide-react";
import EaPhaseStepper from "./EaPhaseStepper";
import EaImportResult from "./EaImportResult";
import EaAssignCompany from "./EaAssignCompany";

const STATUS = {
  QUEUED: { label: "In coda", cls: "bg-slate-100 text-slate-700", Icon: Clock },
  PROCESSING: { label: "In elaborazione", cls: "bg-purple-100 text-purple-800", Icon: Loader2 },
  NEEDS_COMPANY: { label: "Da associare", cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  COMPLETED: { label: "Completato", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  FAILED: { label: "Errore bloccante", cls: "bg-red-100 text-red-700", Icon: XCircle },
  CANCELLED: { label: "Annullato", cls: "bg-slate-100 text-slate-500", Icon: XCircle },
};

export const EaImportRow = ({ job, companies, onChanged }) => {
  const [open, setOpen] = useState(false);
  const s = STATUS[job.status] || STATUS.QUEUED;
  const d = job.detected || {};
  const warnings = job.warnings || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`ea-import-row-${job.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`} data-testid={`ea-import-status-${job.id}`}>
          <s.Icon size={12} className={job.status === "PROCESSING" ? "animate-spin" : ""} /> {s.label}
          {job.status === "QUEUED" && job.queue_position ? ` · #${job.queue_position}` : ""}
        </span>
        <span className="font-medium text-sm text-slate-800 truncate max-w-[340px]" title={job.filename}>{job.filename}</span>
        {d.identifier && <span className="text-xs text-slate-500">{d.identifier_type} {d.identifier} · {d.year}</span>}
        {warnings.length > 0 && (
          <span className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid={`ea-import-warnings-count-${job.id}`}>
            <AlertTriangle size={11} /> {warnings.length} avvisi
          </span>
        )}
        {job.status === "COMPLETED" && <EaVerifyLink companyId={job.company_id} year={d.year} testid={`ea-import-verify-${job.id}`} />}
        <span className="ml-auto text-[11px] text-slate-400">{fmtDateTime(job.created_at)} · {job.created_by_name}</span>
        {(job.status === "COMPLETED" || job.status === "FAILED") && (
          <button onClick={() => setOpen(!open)} className="text-slate-500 hover:text-purple-700" data-testid={`ea-import-toggle-${job.id}`}>
            <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {["QUEUED", "PROCESSING", "FAILED"].includes(job.status) && <div className="mt-2"><EaPhaseStepper phase={job.phase} status={job.status} /></div>}
      {job.status === "FAILED" && job.error && (
        <div className="mt-2 text-xs text-red-700" data-testid={`ea-import-error-${job.id}`}>{job.error}</div>
      )}
      {job.status === "NEEDS_COMPANY" && <EaAssignCompany job={job} companies={companies} onDone={onChanged} />}
      {open && job.status === "COMPLETED" && <EaImportResult job={job} />}
      {open && warnings.length > 0 && (
        <ul className="mt-2 text-xs text-amber-800 list-disc pl-5 space-y-0.5" data-testid={`ea-import-warnings-${job.id}`}>
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </div>
  );
};

export default EaImportRow;
